<template>
  <Modal 
    ref="approvalModalRef"
    @onNegativeActionClick="closeModal"
    @onPositiveActionClick="submitRequest"
    @modalClosed="handleModalClosed"
    :isPositiveActionPending="isSubmitting"
    :isNegativeActionButtonDisabled="isSubmitting"
    :isPositiveActionButtonDisabled="isSubmitting"
    :showIcon="true"
    negativeActionText="Geri Dön"
    positiveActionText="Talebi Gönder"
    :title="!isSubmitting ? 'Erken Ödeme Talebinizin Detayları' : ''"
    class="approvalModal"
    type="info"
  >
    <template v-slot:body>
      <template v-if="!isSubmitting">
        <div class="approval-modal-content">
          <div class="approval-modal-content-item">
            <span>Siparişlerinize Göre Hesaplanan Talep Tutarı</span>
            <span class="item-value">₺{{ calculation?.calculatedRequestAmount }}</span>
          </div>
          <div class="approval-modal-content-item">
            <span>Erken Ödenen Gün Sayısı</span>
            <span class="item-value">{{ calculation?.earlyPaidDayCount }} Gün</span>
          </div>
          <div class="approval-modal-content-item">
            <span>n11 Günlük Hizmet Komisyon Oranı</span>
            <span class="item-value">%{{ calculation?.dailyServiceCommissionRate }}</span>
          </div>
          <div class="approval-modal-content-item">
            <div class="approval-modal-content-item-label">
              <span>Uygulanacak Hizmet Oranı</span>
              <span class="approval-modal-content-item-label-right">
                ({{ calculation?.earlyPaidDayCount }} x %{{ calculation?.dailyServiceCommissionRate }})
              </span>
            </div>
            <span class="item-value">%{{ calculation?.appliedServiceRate }}</span>
          </div>
          <div class="approval-modal-content-item">
            <div class="approval-modal-content-item-label">
              <span>Uygulanacak Hizmet Bedeli</span>
              <span class="approval-modal-content-item-label-right">
                (₺{{ calculation?.calculatedRequestAmount }} x %{{ calculation?.appliedServiceRate }})
              </span>
            </div>
            <span class="item-value">
              <template v-if="Number(calculation?.serviceFee) !== 0">- </template>
              ₺{{ calculation?.serviceFee }}
            </span>
          </div>
          <div class="approval-modal-content-item">
            <div class="approval-modal-content-item-label">
              <span>KDV (%20)</span>
              <span class="approval-modal-content-item-label-right">
                (₺{{ calculation?.serviceFee }} x %20)
              </span>
            </div>
            <span class="item-value">
              <template v-if="Number(calculation?.vatAmount) !== 0">- </template>
              ₺{{ calculation?.vatAmount }}
            </span>
          </div>
          <div class="approval-modal-content-item approval-modal-content-item-total">
            <span>Hesabınıza Yatacak Net Tutar</span>
            <span class="item-value">₺{{ calculation?.netPayableAmount }}</span>
          </div>
        </div>        
        <div class="approval-modal-content-info">
          <img class="info-box-icon" width="20px" height="20px" src="@/assets/images/icons/info-circle.svg" />
          <span>Talebiniz incelenecek ve onaylanması durumunda tutar ertesi gün (bir sonraki iş günü) hesabınıza yatırılacaktır.</span>
        </div>
      </template>
      <template v-else>
        <div class="approval-modal-loading">
          <img src="@/assets/images/loading.gif" alt="Loading" />
        </div>
      </template>
    </template>
  </Modal>
</template>

<script>
import { ref } from 'vue';
import { Modal } from '@n11/so-ui-components';
import { useStore } from 'vuex';
import Request from '@/services/request';
import { EARLY_SETTLEMENT_REQUEST } from '@/utils/enum/endpoints';
import { sendRequestEarlyPaymentSuccessEvent, sendRequestEarlyPaymentDismissEvent, sendRequestEarlyPaymentFailEvent } from '@/utils/ga4/helper';

export default {
  name: "ApprovalModal",
  components: {
    Modal,
  },
  props: {
    calculation: {
      type: Object,
      default: () => null,
    },
    commonGA4Params: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['confirm', 'requestCreated'],
  setup(props, { emit }) {
    const store = useStore();
    const approvalModalRef = ref(null);
    const isSubmitting = ref(false);
    const Req = new Request();
    const isClosingByButton = ref(false);
    
    const openModal = () => {
      if (approvalModalRef.value) {
        approvalModalRef.value.openModal();
      }
    };
    
    const closeModal = () => {
      isClosingByButton.value = true;
      sendRequestEarlyPaymentDismissEvent({
        ...props.commonGA4Params,
        button_name: "Geri Dön",
      });
      
      if (approvalModalRef.value) {
        approvalModalRef.value.hiddenModal();
      }
    };

    const closeModalWithoutEvent = () => {
      isClosingByButton.value = true;
      if (approvalModalRef.value) {
        approvalModalRef.value.hiddenModal();
      }
    };

    const handleModalClosed = () => {
      if (isClosingByButton.value) {
        isClosingByButton.value = false;
        return;
      }
      sendRequestEarlyPaymentDismissEvent({
        ...props.commonGA4Params,
        button_name: "Geri Dön",
      });
    };
    
    const submitRequest = async () => {
      if (isSubmitting.value) return;

      isSubmitting.value = true;

      try {
        const parseNumericValue = (value) => {
          if (value === null || value === undefined || value === '') return 0;
          if (typeof value === 'number') return value;
          
          let strValue = String(value);
          strValue = strValue.replace(/₺/g, '').trim();
          strValue = strValue.replace(/\./g, '').replace(',', '.');
          
          const parsed = parseFloat(strValue);
          return isNaN(parsed) ? 0 : parsed;
        };

        const payload = {
          requestedAmount: parseNumericValue(props.calculation?.calculatedRequestAmount),
        };

        const response = await Req.post(
          EARLY_SETTLEMENT_REQUEST,
          payload,
          {
            baseURL: '/sellerofficeapi/rest',
            headers: { Accept: 'application/json' },
          },
        );

        if (response.status === 200) {
          const row = response.data;

          sendRequestEarlyPaymentSuccessEvent({
            ...props.commonGA4Params,
            button_name: "Talebi Gönder",
            early_payment_id: row?.requestNumber ?? "",
          });

          store.commit('common/SET_TOASTER_DATA', {
            type: 'success',
            title: '',
            description: 'Talebiniz başarıyla iletildi.',
          });

          emit('requestCreated', row);
          emit('confirm', row);
          closeModalWithoutEvent();
        } else {
          throw new Error('Network response was not OK');
        }
      } catch (error) {
        sendRequestEarlyPaymentFailEvent({
          ...props.commonGA4Params,
          button_name: "Talebi Gönder",
        });

        store.commit('common/SET_TOASTER_DATA', {
          type: 'error',
          title: '',
          description: 'Talebiniz iletilirken bir hata oluştu. Lütfen tekrar deneyin.',
        });
        closeModal();
      } finally {
        isSubmitting.value = false;
      }
    };
    
    return {
      approvalModalRef,
      isSubmitting,
      openModal,
      closeModal,
      closeModalWithoutEvent,
      handleModalClosed,
      submitRequest,
    };
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

:deep(.modal-container) {
  max-width: 580px !important;
  width: 100% !important;
  min-width: 480px !important;
}

:deep(.modal) {
  max-width: 580px !important;
  width: 100% !important;
}

:deep(.modal-body) {
  overflow-y: visible !important;
  max-height: none !important;
}

:deep(.modal-footer) {
  button {
    height: 48px !important;
    font-size: 16px !important;
  }
}

.approval-modal-content {
  padding: 4px 0;
  width: 100%;

  &-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    border-bottom: 0.5px solid #C7C7C7;
    padding: 8px;
    flex-wrap: wrap;
    text-align: left;

    &:nth-last-child(2) {
      border-bottom: 0;
    }
    
    &-label {

      .approval-modal-content-item-label-right {
        color: $colorN70;
        font-size: 13px;
        font-weight: 400;
        line-height: 18px;
        letter-spacing: -0.05px;
        font-size: 13px;
      }
    }
    
    span {
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: -0.02px;
      color: $colorN80;
      word-break: break-word;
      flex: 1;
      min-width: 120px;
      
      &.item-value {
        font-weight: 700;
        font-size: 20px;
        line-height: 30px;
        text-align: right;
        margin-left: 0;
        flex: 0 0 auto;
        letter-spacing: -0.2px;
      }
    }
        
    &-total {
      background: $colorM05;
      padding: 8px 10px;
      border-radius: 8px;
      border: none;
      align-items: center;
      
      .item-value {
        color: $colorM50;
        font-size: 24px;
        font-weight: 700;
      }
    }
  }

  &-info {
    width: 100%;
    background-color: $colorB05;
    padding: 16px;
    border-radius: 6px;
    display: flex;
    gap: 8px;
    text-align: left;
    margin-bottom: -5px;
    
    span {
      font-size: 13px;
      color: $colorN80;
      line-height: 18px;
      letter-spacing: -0.05px;
      display: block;
    }
  }
}

.approval-modal-loading {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
}
</style>
